import { readableAccent } from "@eldrun/content";
import { h } from "../kit/dom.ts";
import { compassMark, compassOffset, compassTicks, headingName } from "./compass-model.ts";

/** Um alvo na bússola: aliado (o número do slot), ping (o símbolo) ou chefe (a coroa), na cor dele. */
export interface CompassTarget {
  id: string;
  kind: "aliado" | "ping" | "chefe";
  x: number;
  z: number;
  glyph: string;
  color: string;
}

interface Mark {
  root: HTMLElement;
  glyph: HTMLElement;
  kind: string;
  text: string;
  color: string;
  edge: boolean;
  /** Posição em centésimos de %, para comparar sem montar texto. */
  at: number;
  /** Quadro em que foi visto (as que não foram saem). */
  frame: number;
}

/**
 * Bússola no alto da tela (F81, mockup ui-b): a barra de cristal com o ◆ do rumo no meio, N · L · S · O e os
 * pontos de 22,5° deslizando com a câmera, e os alvos (aliados, pings, chefe) na cor deles. A faixa é feita
 * uma vez e só desliza (`translate`); as marcas são mantidas por id e só mexem no DOM quando mudam (o laço
 * por quadro não monta texto nem conjunto).
 */
export class CompassHud {
  readonly element: HTMLElement;
  private readonly track: HTMLElement;
  private readonly marksLayer: HTMLElement;
  private readonly marks = new Map<string, Mark>();
  private offset = Number.NaN;
  private sector = -1;
  private frame = 0;

  constructor(parent: HTMLElement) {
    this.track = h(
      "div",
      { class: "bussola-faixa" },
      ...compassTicks().map((t) => {
        const tick = h("span", { class: `bussola-marca ${t.kind}` }, t.label);
        tick.style.setProperty("--em", `${(t.at * 100).toFixed(4)}%`);
        return tick;
      }),
    );
    this.marksLayer = h("div", { class: "bussola-alvos" });
    this.element = h("div", { id: "bussola", role: "img", "aria-label": "Bússola" }, h("div", { class: "bussola-janela" }, this.track, this.marksLayer), h("span", { class: "bussola-ornamento", "aria-hidden": "true" }));
    parent.appendChild(this.element);
  }

  /** Por quadro: o rumo da câmera (`heading`, rad) e de onde ela olha. */
  update(heading: number, fromX: number, fromZ: number, targets: readonly CompassTarget[]): void {
    const offset = Math.round(compassOffset(heading) * 10000);
    if (offset !== this.offset) {
      this.offset = offset;
      this.track.style.transform = `translateX(${(offset / 100).toFixed(2)}%)`;
    }
    const sector = Math.round(heading / (Math.PI / 4));
    if (sector !== this.sector) {
      this.sector = sector;
      this.element.setAttribute("aria-label", `Bússola: rumo ${headingName(heading)}`);
    }
    const frame = ++this.frame;
    for (const t of targets) {
      const m = compassMark(heading, fromX, fromZ, t.x, t.z);
      if (!m) continue;
      let mark = this.marks.get(t.id);
      if (!mark) {
        const glyph = h("span", { class: "bussola-glifo" });
        mark = { root: h("span", { class: "bussola-alvo" }, glyph), glyph, kind: "", text: "", color: "", edge: false, at: Number.NaN, frame };
        this.marks.set(t.id, mark);
        this.marksLayer.appendChild(mark.root);
      }
      mark.frame = frame;
      if (t.kind !== mark.kind || m.edge !== mark.edge) {
        mark.kind = t.kind;
        mark.edge = m.edge;
        mark.root.className = `bussola-alvo ${t.kind}${m.edge ? " borda" : ""}`;
      }
      if (t.color !== mark.color) {
        mark.color = t.color;
        mark.root.style.setProperty("--cor", t.color);
        mark.root.style.setProperty("--cor-claro", readableAccent(t.color));
      }
      if (t.glyph !== mark.text) {
        mark.text = t.glyph;
        mark.glyph.textContent = t.glyph;
      }
      const at = Math.round((50 + m.x * 50) * 100);
      if (at !== mark.at) {
        mark.at = at;
        mark.root.style.left = `${(at / 100).toFixed(2)}%`;
      }
    }
    for (const [id, mark] of this.marks) {
      if (mark.frame === frame) continue;
      mark.root.remove();
      this.marks.delete(id);
    }
  }

  remove(): void {
    this.element.remove();
  }
}
